import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useNotificationsContext } from "@/contexts/NotificationsContext";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Send, Loader2, MessageCircle, CheckCircle2, Lock, ArrowLeft, Plane, Building2, Car, Utensils, MoreHorizontal, Pin, ArrowRight, ChevronDown, ChevronUp, Archive } from "lucide-react";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { format, isToday, isYesterday } from "date-fns";
import BottomNav from "@/components/BottomNav";
import { FileUpload } from "@/components/chat/FileUpload";
import { MessageAttachment } from "@/components/chat/MessageAttachment";
import { TruncatedMessage } from "@/components/chat/TruncatedMessage";
import { ScrollToBottomButton } from "@/components/chat/ScrollToBottomButton";
import { MessageStatus } from "@/components/chat/MessageStatus";
import { TypingIndicator } from "@/components/chat/TypingIndicator";
import { MessageAvatar } from "@/components/chat/MessageAvatar";
import { SystemMessage } from "@/components/chat/SystemMessage";
import { DeletedMessage } from "@/components/chat/DeletedMessage";
import { SelectionToolbar } from "@/components/chat/SelectionToolbar";
import { SelectableMessage } from "@/components/chat/SelectableMessage";
import { DeleteSelectedDialog } from "@/components/chat/DeleteSelectedDialog";
import { useTypingIndicator } from "@/hooks/useTypingIndicator";
import { useMessageSelection } from "@/hooks/useMessageSelection";
import { useMessageSync, SyncMessage } from "@/hooks/useMessageSync";

interface Service {
  id: string;
  name: string;
  welcome_message: string | null;
}

interface Conversation {
  id: string;
  user_id: string;
  status: "open" | "pending" | "closed";
  subject: string | null;
  unread_count_user: number;
  service_id: string | null;
  is_archived?: boolean;
  archived_at?: string | null;
  assigned_agent_id?: string | null;
  service?: Service | null;
}

interface AgentProfile {
  avatar_url: string | null;
  first_name: string | null;
  last_name: string | null;
  display_name: string | null;
}

export default function ChatPage() {
  // Log version on mount to confirm new code is running
  useEffect(() => {
    console.log("[ChatPage] Version 1.0.4 loaded - useMessageSync active");
  }, []);
  
  const { user, isAgent } = useAuth();
  const { refreshUnreadCount } = useNotificationsContext();
  const navigate = useNavigate();
  const { conversationId } = useParams();
  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [agentProfile, setAgentProfile] = useState<AgentProfile | null>(null);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [showPinnedPanel, setShowPinnedPanel] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const hasMessagesSentRef = useRef(false);
  
  // Refs for stable function access
  const scrollToBottomRef = useRef<(behavior?: ScrollBehavior) => void>(() => {});
  const refreshUnreadCountRef = useRef(refreshUnreadCount);

  // Message sync hook - handles realtime + polling
  const { messages, setMessages, isLoading: messagesLoading, refetch } = useMessageSync({
    conversationId: conversation?.id,
    userId: user?.id,
    pollingIntervalMs: 3000,
    enabled: !!conversation?.id && !!user?.id,
  });

  // Message selection hook
  const {
    selectedMessageIds,
    isSelectionMode,
    toggleMessageSelection,
    selectMessage,
    clearSelection,
    isSelected,
  } = useMessageSelection();

  // Typing indicator
  const { typingUsers, sendTyping, stopTyping, isTyping } = useTypingIndicator(conversationId || "");

  // Track if user has sent at least one message
  useEffect(() => {
    if (messages.length > 0 && messages.some(m => !m.is_from_agent)) {
      hasMessagesSentRef.current = true;
    }
  }, [messages]);

  // Cleanup empty conversation on unmount
  useEffect(() => {
    const convId = conversationId;
    const hasSentMessages = hasMessagesSentRef.current;
    
    return () => {
      if (convId && !hasSentMessages) {
        const timeoutId = setTimeout(async () => {
          const { data: msgCheck } = await supabase
            .from("messages")
            .select("id")
            .eq("conversation_id", convId)
            .limit(1);
          
          if (!msgCheck || msgCheck.length === 0) {
            await supabase.from("conversations").delete().eq("id", convId);
          }
        }, 2000);
      }
    };
  }, [conversationId]);

  const scrollToBottom = useCallback((behavior: ScrollBehavior = "smooth") => {
    messagesEndRef.current?.scrollIntoView({ behavior });
  }, []);

  scrollToBottomRef.current = scrollToBottom;
  refreshUnreadCountRef.current = refreshUnreadCount;

  const scrollToMessage = useCallback((messageId: string) => {
    const messageElement = document.getElementById(`message-${messageId}`);
    if (messageElement) {
      messageElement.scrollIntoView({ behavior: "smooth", block: "center" });
      messageElement.classList.add("ring-2", "ring-primary", "ring-offset-2");
      setTimeout(() => {
        messageElement.classList.remove("ring-2", "ring-primary", "ring-offset-2");
      }, 2000);
    }
  }, []);

  const handleScroll = useCallback(() => {
    if (messagesContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
      setShowScrollButton(distanceFromBottom > window.innerHeight);
    }
  }, []);

  // Fetch conversation data
  useEffect(() => {
    if (!user) return;

    const fetchData = async () => {
      if (!conversationId) {
        navigate("/chat", { replace: true });
        return;
      }

      const { data, error } = await supabase
        .from("conversations")
        .select(`*, service:services(id, name, welcome_message, bg_image_url)`)
        .eq("id", conversationId)
        .eq("user_id", user.id)
        .maybeSingle();
      
      if (error || !data) {
        navigate("/chat", { replace: true });
        return;
      }

      setConversation(data as Conversation);
      
      if (data.assigned_agent_id) {
        const { data: agentData } = await supabase
          .from("profiles")
          .select("avatar_url, first_name, last_name, display_name")
          .eq("id", data.assigned_agent_id)
          .maybeSingle();
        
        if (agentData) setAgentProfile(agentData);
      }

      // Pre-fill welcome message if no messages
      if (data.service?.welcome_message) {
        // This will be set after messages load if empty
      }

      // Mark messages as read
      await supabase
        .from("messages")
        .update({ is_read: true, read_at: new Date().toISOString() })
        .eq("conversation_id", data.id)
        .eq("is_from_agent", true)
        .eq("is_read", false);

      await supabase
        .from("conversations")
        .update({ unread_count_user: 0 })
        .eq("id", data.id);

      setTimeout(() => refreshUnreadCount(), 100);
      setLoading(false);
    };

    fetchData();
  }, [user, conversationId, navigate, refreshUnreadCount]);

  // Reset initial load state when conversation changes
  useEffect(() => {
    setInitialLoadComplete(false);
  }, [conversationId]);

  // Mark initial load as complete once messages have been fetched
  useEffect(() => {
    if (!messagesLoading && conversation?.id) {
      setInitialLoadComplete(true);
    }
  }, [messagesLoading, conversation?.id]);

  // Pre-fill welcome message ONLY for truly new conversations
  useEffect(() => {
    // Only pre-fill if:
    // 1. Initial load is complete (messages have been fetched at least once)
    // 2. There are truly no messages in the database
    // 3. There's a welcome message to show
    if (initialLoadComplete && !messagesLoading && messages.length === 0 && conversation?.service?.welcome_message) {
      setNewMessage(conversation.service.welcome_message);
    }
  }, [initialLoadComplete, messagesLoading, messages.length, conversation?.service?.welcome_message]);

  // Scroll to bottom after initial load
  useEffect(() => {
    if (!loading && !messagesLoading && messages.length > 0) {
      setTimeout(() => scrollToBottom("instant"), 100);
    }
  }, [loading, messagesLoading, scrollToBottom]);

  // Subscribe to conversation status changes
  useEffect(() => {
    if (!conversation?.id) return;

    const convChannel = supabase
      .channel(`conv-status-client-${conversation.id}`)
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "conversations",
          filter: `id=eq.${conversation.id}`,
        },
        (payload) => {
          const updated = payload.new as Conversation;
          setConversation(prev => prev ? { ...prev, ...updated } : null);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(convChannel);
    };
  }, [conversation?.id]);

  // Handle delete completion
  const handleDeleteComplete = useCallback((deletedIds: string[], forEveryone: boolean) => {
    if (forEveryone) {
      // Mark as deleted in state
      setMessages(prev => prev.map(m => 
        deletedIds.includes(m.id) 
          ? { ...m, is_deleted: true, deleted_at: new Date().toISOString(), deleted_by: user?.id || null } 
          : m
      ));
    } else {
      // Remove from local state
      setMessages(prev => prev.filter(m => !deletedIds.includes(m.id)));
    }
    clearSelection();
  }, [user?.id, setMessages, clearSelection]);

  // Check if user can delete for everyone (only own messages)
  const canDeleteForEveryone = useCallback(() => {
    if (!user) return false;
    for (const id of selectedMessageIds) {
      const msg = messages.find(m => m.id === id);
      if (msg && msg.sender_id !== user.id) {
        return false;
      }
    }
    return true;
  }, [selectedMessageIds, messages, user]);

  const handleSend = async () => {
    if (!newMessage.trim() || !conversation || !user || sending) return;

    setSending(true);
    const messageContent = newMessage.trim();
    setNewMessage("");

    const { error } = await supabase.from("messages").insert({
      conversation_id: conversation.id,
      sender_id: user.id,
      content: messageContent,
      is_from_agent: false,
      delivered_at: new Date().toISOString(),
    });

    if (error) {
      toast.error("Échec de l'envoi du message");
      setNewMessage(messageContent);
    } else {
      setTimeout(() => scrollToBottom(), 100);
    }

    setSending(false);
    inputRef.current?.focus();
  };

  const formatMessageTime = (dateStr: string) => format(new Date(dateStr), "HH:mm");

  const formatDateHeader = (dateStr: string) => {
    const date = new Date(dateStr);
    if (isToday(date)) return "Aujourd'hui";
    if (isYesterday(date)) return "Hier";
    return format(date, "d MMMM yyyy");
  };

  const getDateGroups = () => {
    const groups: { date: string; messages: SyncMessage[] }[] = [];
    let currentDate = "";

    messages.forEach((msg) => {
      const msgDate = format(new Date(msg.created_at), "yyyy-MM-dd");
      if (msgDate !== currentDate) {
        currentDate = msgDate;
        groups.push({ date: msg.created_at, messages: [msg] });
      } else {
        groups[groups.length - 1].messages.push(msg);
      }
    });

    return groups;
  };

  const getStatusDisplay = () => {
    if (!conversation) return null;
    
    switch (conversation.status) {
      case "open":
        return {
          label: "En cours",
          icon: <MessageCircle size={14} />,
          className: "bg-success/10 text-success border-success/20"
        };
      case "pending":
        return {
          label: "En attente",
          icon: <MessageCircle size={14} />,
          className: "bg-warning/10 text-warning border-warning/20"
        };
      default:
        return null;
    }
  };

  if (loading || messagesLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const statusDisplay = getStatusDisplay();
  const isArchived = conversation?.is_archived;
  const pinnedMessages = messages.filter(m => m.is_pinned && !m.is_deleted);
  // Filter selectable messages (not system, not deleted)
  const selectableMessages = messages.filter(m => !m.is_system_message && !m.is_deleted);

  return (
    <div className="h-screen flex flex-col bg-background overflow-hidden">
      {/* Selection Toolbar */}
      {isSelectionMode && (
        <SelectionToolbar
          selectedCount={selectedMessageIds.size}
          onClearSelection={clearSelection}
          onDeleteClick={() => setDeleteDialogOpen(true)}
        />
      )}

      {/* Header */}
      <header 
        className={`shrink-0 z-10 glass border-b border-border px-3 sm:px-4 py-2.5 sm:py-3 ${isSelectionMode ? 'mt-14' : ''}`}
        style={{ paddingTop: isSelectionMode ? '0.625rem' : 'max(2.5rem, calc(env(safe-area-inset-top, 0px) + 1rem))' }}
      >
        <div className="flex items-center gap-2 sm:gap-3 w-full">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate("/chat")}
            className="shrink-0 h-9 w-9"
          >
            <ArrowLeft size={18} />
          </Button>
          <Avatar className="w-9 h-9 sm:w-10 sm:h-10">
            <AvatarImage src="/pwa-192x192.png" alt="Concierge" />
            <AvatarFallback className="bg-primary/10 text-primary font-serif font-semibold text-sm sm:text-base">
              C
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
              <h1 className="font-serif font-medium text-foreground text-sm sm:text-base">
                {agentProfile?.display_name || "Votre concierge"}
              </h1>
              {conversation?.service?.name && (
                <Badge variant="outline" className="bg-primary/10 text-primary border-primary/20 text-[9px] sm:text-[10px] hidden xs:inline-flex">
                  <span className="ml-1">{conversation.service.name}</span>
                </Badge>
              )}
              {statusDisplay && (
                <Badge variant="outline" className={`text-[9px] sm:text-[10px] ${statusDisplay.className}`}>
                  {statusDisplay.icon}
                  <span className="ml-1">{statusDisplay.label}</span>
                </Badge>
              )}
              {isArchived && (
                <Badge variant="outline" className="bg-muted text-muted-foreground border-muted text-[9px] sm:text-[10px]">
                  <Archive size={10} className="mr-1" />
                  Traitée
                </Badge>
              )}
            </div>
            <p className="text-[10px] sm:text-xs text-muted-foreground truncate">
              {isArchived 
                ? "Répondez pour réactiver cette conversation"
                : "Nous répondons généralement en quelques minutes"
              }
            </p>
          </div>
          {!isArchived && (
            <div className="w-2 h-2 rounded-full bg-success animate-pulse shrink-0" />
          )}
        </div>
      </header>

      {/* Pinned Messages Bar */}
      {pinnedMessages.length > 0 && !isSelectionMode && (
        <div className="bg-primary/5 border-b border-primary/20">
          <button 
            className="w-full px-4 py-2 flex items-center justify-between hover:bg-primary/10 transition-colors"
            onClick={() => setShowPinnedPanel(!showPinnedPanel)}
          >
            <div className="flex items-center gap-2 text-xs text-primary font-medium">
              <Pin size={12} />
              <span>{pinnedMessages.length} message{pinnedMessages.length > 1 ? "s" : ""} épinglé{pinnedMessages.length > 1 ? "s" : ""}</span>
            </div>
            {showPinnedPanel ? <ChevronUp size={14} className="text-primary" /> : <ChevronDown size={14} className="text-primary" />}
          </button>
          {showPinnedPanel && (
            <div className="px-4 pb-3 space-y-2 max-h-48 overflow-auto">
              {pinnedMessages.map((msg) => (
                <div 
                  key={`pinned-${msg.id}`} 
                  className="bg-background rounded-lg px-3 py-2 flex items-start justify-between gap-2 group"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-foreground line-clamp-2">{msg.content}</p>
                    <span className="text-[10px] text-muted-foreground">
                      {msg.is_from_agent ? "Concierge" : "Vous"} • {formatMessageTime(msg.created_at)}
                    </span>
                  </div>
                  <button
                    className="h-7 px-2 text-xs flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0 text-primary hover:bg-primary/10 rounded"
                    onClick={() => {
                      scrollToMessage(msg.id);
                      setShowPinnedPanel(false);
                    }}
                  >
                    Aller
                    <ArrowRight size={12} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Messages */}
      <main 
        ref={messagesContainerRef}
        onScroll={handleScroll}
        className="flex-1 overflow-auto px-3 sm:px-4 py-3 sm:py-4"
        style={{ paddingBottom: 'calc(8rem + max(20px, env(safe-area-inset-bottom, 0px)))' }}
      >
        <div className="w-full max-w-4xl mx-auto">
          {messages.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center px-4 sm:px-8">
              <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4 overflow-hidden">
                <img src="/pwa-192x192.png" alt="Concierge" className="w-full h-full object-cover" />
              </div>
              <h2 className="font-serif text-lg sm:text-xl font-medium text-foreground mb-2">
                {conversation?.service?.name || "Bienvenue"}
              </h2>
              <p className="text-xs sm:text-sm text-muted-foreground max-w-xs">
                Votre assistant personnel est prêt à vous aider. Envoyez un message pour commencer.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {getDateGroups().map((group, groupIndex) => (
                <div key={groupIndex}>
                  <div className="flex justify-center my-4">
                    <span className="text-xs text-muted-foreground bg-muted px-3 py-1 rounded-full">
                      {formatDateHeader(group.date)}
                    </span>
                  </div>

                  <div className="space-y-2">
                    {group.messages.map((msg) => {
                      if (msg.is_system_message) {
                        return (
                          <SystemMessage 
                            key={msg.id}
                            content={msg.content} 
                            createdAt={msg.created_at} 
                          />
                        );
                      }
                      
                      if (msg.is_deleted) {
                        return (
                          <DeletedMessage 
                            key={msg.id}
                            createdAt={msg.created_at}
                            isFromAgent={msg.is_from_agent}
                            deletedForEveryone={true}
                          />
                        );
                      }
                      
                      const isOwnMessage = msg.sender_id === user?.id;
                      
                      return (
                        <SelectableMessage
                          key={msg.id}
                          messageId={msg.id}
                          isSelected={isSelected(msg.id)}
                          isSelectionMode={isSelectionMode}
                          onSelect={selectMessage}
                          onToggle={toggleMessageSelection}
                          isFromCurrentUser={!msg.is_from_agent}
                        >
                          <div
                            id={`message-${msg.id}`}
                            className={`flex items-end gap-1.5 ${
                              msg.is_from_agent ? "justify-start" : "justify-end"
                            } animate-fade-in transition-all duration-300`}
                          >
                            {msg.is_from_agent && (
                              <MessageAvatar 
                                isFromAgent={true} 
                                avatarUrl="/pwa-192x192.png"
                                firstName="C"
                                lastName=""
                                size="xs" 
                              />
                            )}
                            <div
                              className={`max-w-[75%] px-4 py-2.5 relative ${
                                msg.is_from_agent ? "chat-bubble-agent" : "chat-bubble-user"
                              } ${msg.is_pinned ? "ring-1 ring-primary/30" : ""}`}
                            >
                              {msg.is_pinned && (
                                <Pin size={10} className="absolute top-1 right-1 text-primary" />
                              )}
                              <TruncatedMessage content={msg.content} maxHeight={50} />
                              {msg.attachment_url && msg.attachment_name && msg.attachment_type && (
                                <MessageAttachment
                                  url={msg.attachment_url}
                                  name={msg.attachment_name}
                                  type={msg.attachment_type}
                                  isFromAgent={msg.is_from_agent}
                                />
                              )}
                              <div className={`flex items-center gap-1 mt-1 ${msg.is_from_agent ? "justify-start" : "justify-end"}`}>
                                <span className={`text-[10px] ${msg.is_from_agent ? "text-muted-foreground" : "text-primary-foreground/70"}`}>
                                  {formatMessageTime(msg.created_at)}
                                </span>
                                {!msg.is_from_agent && (
                                  <MessageStatus
                                    createdAt={msg.created_at}
                                    deliveredAt={msg.delivered_at}
                                    readAt={msg.read_at}
                                    isRead={msg.is_read || false}
                                    isFromAgent={msg.is_from_agent}
                                  />
                                )}
                              </div>
                            </div>
                            {!msg.is_from_agent && (
                              <MessageAvatar isFromAgent={false} size="xs" />
                            )}
                          </div>
                        </SelectableMessage>
                      );
                    })}
                  </div>
                </div>
              ))}
              
              {isTyping && (
                <div className="flex justify-start">
                  <div className="bg-muted rounded-2xl px-4 py-2">
                    <TypingIndicator typingUsers={typingUsers} />
                  </div>
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>
      </main>

      <ScrollToBottomButton 
        visible={showScrollButton} 
        onClick={() => scrollToBottom()} 
      />

      {/* Input */}
      <div 
        className="fixed left-0 right-0 glass border-t border-border px-3 sm:px-4 py-2 z-20"
        style={{ bottom: 'calc(4rem + max(20px, env(safe-area-inset-bottom, 0px)))' }}
      >
        <div className="w-full max-w-4xl mx-auto">
          <div className="flex items-end gap-2">
            <FileUpload
              disabled={sending}
              onFileUploaded={async (url, name, type) => {
                if (!conversation || !user) return;
                const { error } = await supabase.from("messages").insert({
                  conversation_id: conversation.id,
                  sender_id: user.id,
                  content: `📎 ${name}`,
                  is_from_agent: false,
                  attachment_url: url,
                  attachment_name: name,
                  attachment_type: type,
                  delivered_at: new Date().toISOString(),
                });
                if (error) {
                  toast.error("Échec de l'envoi du fichier");
                }
              }}
            />
            <div className="flex-1">
              <Textarea
                ref={inputRef}
                value={newMessage}
                onChange={(e) => {
                  setNewMessage(e.target.value);
                  sendTyping();
                  e.target.style.height = 'auto';
                  e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
                onBlur={stopTyping}
                placeholder={isArchived ? "Répondre pour réactiver..." : "Votre message..."}
                rows={1}
                className="w-full min-h-[44px] max-h-[120px] py-2.5 px-4 resize-none rounded-2xl bg-muted/50 border-0 focus-visible:ring-1 focus-visible:ring-primary/30 overflow-y-auto"
                style={{ height: '44px' }}
              />
            </div>
            <Button
              variant="gold"
              size="icon"
              onClick={handleSend}
              disabled={!newMessage.trim() || sending}
              className="shrink-0 h-11 w-11 rounded-full"
            >
              {sending ? (
                <Loader2 size={18} className="animate-spin" />
              ) : (
                <Send size={18} />
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Delete Selected Dialog */}
      <DeleteSelectedDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        selectedMessageIds={selectedMessageIds}
        userId={user?.id || ""}
        conversationId={conversation?.id || ""}
        canDeleteForEveryone={canDeleteForEveryone()}
        onDeleteComplete={handleDeleteComplete}
        onDeleted={refetch}
      />

      <BottomNav />
    </div>
  );
}
